// ─── Runtime Metrics ─────────────────────────────────────────────────────────
//
// Lightweight performance and health metrics for the Phase 3 runtime.
// All metrics are in-process, in-memory, and reset on clearMetrics().
// No external telemetry. No PII. No sampling - every event is counted.

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RuntimeMetrics {
  actionCount: number;
  stateChangeCount: number;
  eventCount: number;
  errorCount: number;
  actionLatencyMs: number[];
  averageActionLatencyMs: number;
  p95ActionLatencyMs: number;
  uptimeMs: number;
}

// ─── Module State ─────────────────────────────────────────────────────────────

let _startedAt = Date.now();
let _actionCount = 0;
let _stateChangeCount = 0;
let _eventCount = 0;
let _errorCount = 0;
const _latencies: number[] = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeP95(latencies: number[]): number {
  if (latencies.length === 0) return 0;
  const sorted = [...latencies].sort((a, b) => a - b);
  const idx = Math.floor(sorted.length * 0.95);
  return sorted[Math.min(idx, sorted.length - 1)];
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Record a completed action with its latency in milliseconds. */
export function recordAction(latencyMs: number): void {
  _actionCount++;
  if (latencyMs >= 0) {
    _latencies.push(latencyMs);
    // Keep at most 10k latency samples to bound memory
    if (_latencies.length > 10_000) {
      _latencies.shift();
    }
  }
}

/** Record a state key change. */
export function recordStateChange(): void {
  _stateChangeCount++;
}

/** Record an event emitted on the bus. */
export function recordEvent(): void {
  _eventCount++;
}

/** Record a runtime error. */
export function recordError(): void {
  _errorCount++;
}

/** Returns the current metrics snapshot. */
export function getMetrics(): RuntimeMetrics {
  const avg = _latencies.length > 0
    ? _latencies.reduce((a, b) => a + b, 0) / _latencies.length
    : 0;

  return {
    actionCount: _actionCount,
    stateChangeCount: _stateChangeCount,
    eventCount: _eventCount,
    errorCount: _errorCount,
    actionLatencyMs: [..._latencies],
    averageActionLatencyMs: Math.round(avg * 100) / 100,
    p95ActionLatencyMs: computeP95(_latencies),
    uptimeMs: Date.now() - _startedAt,
  };
}

/** Reset all metrics counters. Resets uptime timer. */
export function resetMetrics(): void {
  _actionCount = 0;
  _stateChangeCount = 0;
  _eventCount = 0;
  _errorCount = 0;
  _latencies.length = 0;
  _startedAt = Date.now();
}
